/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { ObservableErrorResourceParser } from '../util/errors';
import { FieldValidationError, PagingRequest, QueryResult, ResourceQueryResult, Services } from '../util/services';
import { List, Map as ImmutableMap, Set } from 'immutable';
import { OffsetDateTime } from '../util/dates';
import { IdentityMessage } from '../util/messages';
import { DqlQuery, Query } from '../query/field';
import { FilterField } from '../query/filterfields';
import { OrderField } from '../query/orderfields';
import { GlobalCalendarEventResource, GlobalCalendarEventResourceService } from './global-calendar-event-resource.service';

/* eslint-enable */

@Injectable()
export class GlobalCalendarEventService implements GlobalCalendarEvent.Service {

  private readonly filterField = new FilterField.GlobalCalendarEvent();
  private readonly orderField = new OrderField.GlobalCalendarEvent();
  private readonly fields = new GlobalCalendarEvent.Fields.GlobalCalendarEvent();

  constructor(private resourceService: GlobalCalendarEventResourceService) {
  }

  query(request: GlobalCalendarEvent.QueryRequest): Observable<QueryResult<GlobalCalendarEvent.GlobalCalendarEvent>> {
    return Observable.create((observer: Observer<QueryResult<GlobalCalendarEvent.GlobalCalendarEvent>>) => {
      const resourceRequest = GlobalCalendarEvent.toResourceQueryRequest(this.filterField, this.orderField, this.fields, request);
      return this.resourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<GlobalCalendarEventResource.GlobalCalendarEvent>) => {
          observer.next({
            items: List.of(...result.items.map((item) => GlobalCalendarEvent.toPublic(item))),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  get(request: GlobalCalendarEvent.GetRequest): Observable<GlobalCalendarEvent.GlobalCalendarEvent> {
    return Observable.create((observer: Observer<GlobalCalendarEvent.GlobalCalendarEvent>) => {
      const resourceRequest = GlobalCalendarEvent.toResourceGetRequest(this.fields, request);
      return this.resourceService.get(resourceRequest).subscribe(
        (result: GlobalCalendarEventResource.GlobalCalendarEvent) => {
          observer.next(GlobalCalendarEvent.toPublic(result));
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  create(request: GlobalCalendarEvent.CreateRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = GlobalCalendarEvent.toResourceCreateRequest(request);
      return this.resourceService.create(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  update(request: GlobalCalendarEvent.UpdateRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = GlobalCalendarEvent.toResourceUpdateRequest(request);
      return this.resourceService.update(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  delete(request: GlobalCalendarEvent.DeleteRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = request;
      return this.resourceService.delete(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  private translateError(error: any): any {
    const res = ObservableErrorResourceParser.parseError(error);
    const fieldErrors = ObservableErrorResourceParser.extractFieldErrors(res);
    const fieldErrorMap = ObservableErrorResourceParser.toFieldErrorMap(Keys.toValidatedField, fieldErrors);
    if (!fieldErrorMap.isEmpty()) {
      return FieldValidationError.of(fieldErrorMap);
    }
    return error;
  }
}

export namespace GlobalCalendarEvent {


  export interface Service {

    // <editor-fold desc="CRUD">

    query(request: QueryRequest): Observable<QueryResult<GlobalCalendarEvent>>;

    get(request: GetRequest): Observable<GlobalCalendarEvent>;

    create(request: CreateRequest): Observable<IdentityMessage>;

    // </editor-fold>

  }

  // <editor-fold desc="CRUD">

  export interface QueryRequest {
    fields?: Query.FieldFunction<Fields.GlobalCalendarEvent>;
    filter?: Query.FilterFunction<FilterField.GlobalCalendarEvent>;
    order?: Query.OrderFunction<OrderField.GlobalCalendarEvent>;
    paging?: PagingRequest;
    rights?: Set<string>;
    noProgressBar?: boolean;
  }

  export interface GetRequest {
    id: number;
    fields?: Query.FieldFunction<Fields.GlobalCalendarEvent>;
    rights?: Set<string>;
  }

  export interface GlobalCalendarEvent {
    id: number;
    grantedRights: Set<string>;
    startTime: OffsetDateTime;
    endTime: OffsetDateTime;
    creationTime: OffsetDateTime;
    updateTime: OffsetDateTime;
    title: string;
    comment?: string;
    color: number;
    creatorUser: {
      id: number;
      userName: string;
      personName: string;
    };
  }

  export interface CreateRequest {
    startTime: OffsetDateTime;
    endTime: OffsetDateTime;
    title: string;
    comment?: string;
    color: number;
  }

  export interface UpdateRequest {
    id: number;
    startTime: OffsetDateTime;
    endTime: OffsetDateTime;
    title: string;
    comment?: string;
    color: number;
  }

  export interface DeleteRequest {
    id: number;
  }

  export function toResourceQueryRequest(
    filterField: FilterField.GlobalCalendarEvent,
    orderField: OrderField.GlobalCalendarEvent,
    fields: GlobalCalendarEvent.Fields.GlobalCalendarEvent,
    request: QueryRequest): GlobalCalendarEventResource.QueryRequest {
    const filter: string | undefined = DqlQuery.toOptionalFilter(filterField, request.filter);
    const order: string | undefined = DqlQuery.toOptionalOrder(orderField, request.order);
    const field: string | undefined = DqlQuery.toOptionalFields(fields, request.fields);
    return {
      filter: filter,
      order: order,
      fields: field,
      page_number: request.paging ? request.paging.pageNumber : undefined,
      number_of_items: request.paging ? request.paging.numberOfItems : undefined,
      rights: DqlQuery.RightRequestSerializer.getInstance().serialize(request.rights),
      no_progress_bar: request.noProgressBar
    };
  }

  export function toResourceGetRequest(
    fields: GlobalCalendarEvent.Fields.GlobalCalendarEvent,
    request: GetRequest): GlobalCalendarEventResource.GetRequest {
    const field: string | undefined = DqlQuery.toOptionalFields(fields, request.fields);
    return {
      id: request.id,
      fields: field,
      rights: DqlQuery.RightRequestSerializer.getInstance().serialize(request.rights)
    };
  }

  export function toResourceCreateRequest(request: CreateRequest): GlobalCalendarEventResource.CreateRequest {
    return {
      start_time: Services.offsetDateTimeToString(request.startTime)!,
      end_time: Services.offsetDateTimeToString(request.endTime)!,
      title: request.title,
      color: request.color,
      comment: request.comment
    };
  }

  export function toResourceUpdateRequest(request: UpdateRequest): GlobalCalendarEventResource.UpdateRequest {
    return {
      id: request.id,
      start_time: Services.offsetDateTimeToString(request.startTime)!,
      end_time: Services.offsetDateTimeToString(request.endTime)!,
      title: request.title,
      color: request.color,
      comment: request.comment
    };
  }


  export function toPublic(r: GlobalCalendarEventResource.GlobalCalendarEvent): GlobalCalendarEvent.GlobalCalendarEvent {
    return {
      id: r.id,
      grantedRights: r.granted_rights ? Set.of(...r.granted_rights) : Set.of(),
      startTime: Services.toOffsetDateTime(r.start_time),
      endTime: Services.toOffsetDateTime(r.end_time),
      creationTime: Services.toOffsetDateTime(r.creation_time),
      updateTime: Services.toOffsetDateTime(r.update_time),
      title: r.title,
      comment: r.comment,
      color: r.color,
      creatorUser: {
        id: r.creator_user.id,
        personName: r.creator_user.person_name,
        userName: r.creator_user.user_name
      }
    };
  }

  export enum ValidatedField {
    UNKNOWN,
    TITLE,
    START_TIME,
    END_TIME,
    COMMENT,
    COLOR
  }

  export namespace Fields {

    export class GlobalCalendarEvent {

      readonly id: Query.Field = new DqlQuery.Field('id');
      readonly grantedRights: Query.Field = new DqlQuery.Field('granted_rights');
      readonly startTime: Query.Field = new DqlQuery.Field('start_time');
      readonly endTime: Query.Field = new DqlQuery.Field('end_time');
      readonly title: Query.Field = new DqlQuery.Field('title');
      readonly color: Query.Field = new DqlQuery.Field('color');
      readonly comment: Query.Field = new DqlQuery.Field('comment');
      readonly creatorUser: Query.Field = new DqlQuery.Field('creator_user');
      readonly creationTime: Query.Field = new DqlQuery.Field('creation_time');
      readonly updateTime: Query.Field = new DqlQuery.Field('update_time');

      get each(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.startTime,
          this.endTime,
          this.title,
          this.comment,
          this.color,
          this.creatorUser,
          this.creationTime,
          this.updateTime,
        );
      }
    }

  }

}

// <editor-fold desc="Internal">


class Keys {

  private static readonly TITLE = 'title';
  private static readonly START_TIME = 'start_time';
  private static readonly END_TIME = 'end_time';
  private static readonly COMMENT = 'comment';
  private static readonly COLOR = 'color';

  private static readonly keyValidatedFieldMap: ImmutableMap<string, GlobalCalendarEvent.ValidatedField> = ImmutableMap.of(
    Keys.START_TIME, GlobalCalendarEvent.ValidatedField.START_TIME,
    Keys.END_TIME, GlobalCalendarEvent.ValidatedField.END_TIME,
    Keys.COMMENT, GlobalCalendarEvent.ValidatedField.COMMENT,
    Keys.COLOR, GlobalCalendarEvent.ValidatedField.COLOR,
    Keys.TITLE, GlobalCalendarEvent.ValidatedField.TITLE,
  );

  public static toValidatedField(fieldKey: string): GlobalCalendarEvent.ValidatedField {
    return Keys.keyValidatedFieldMap.get(fieldKey, GlobalCalendarEvent.ValidatedField.UNKNOWN);
  }

}

// </editor-fold>


